import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { APP_CONSTANTS } from '../../../core/constants/app.constants';
import { Constants } from '../../../core/constants/core.constants';
import { ReferenceDataEntity } from '../../../core/enums/reference-data-entity.enum';
import { AuthService } from '../../../core/Provider/auth.service';
import { getStudentOnlyAccessMessage } from '../../../core/utils/staff-preview.utils';
import { getReferenceDataItems } from '../../../core/utils/reference-data.utils';
import { DocumentViewService } from '../../../services/document-view.service';
import { StudentService } from '../../../services/student.service';
import { StudentActivityTrackingService } from '../../../services/student-activity-tracking.service';
import { StudentActivityType } from '../../students/student.model';
import { PdfViewerModalComponent } from '../../student-onboarding/pdf-viewer-modal/pdf-viewer-modal.component';
import { ReferenceDataItem } from '../../reports/reports.model';

@Component({
  selector: 'app-student-complaint',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DialogModule, SelectModule, PdfViewerModalComponent],
  templateUrl: './student-complaint.component.html',
  styleUrl: './student-complaint.component.scss'
})
export class StudentComplaintComponent implements OnChanges, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly studentService = inject(StudentService);
  private readonly documentViewService = inject(DocumentViewService);
  private readonly messageService = inject(MessageService);
  private readonly activityTracking = inject(StudentActivityTrackingService);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  readonly eyeIcon = Constants.viewIcon;
  readonly downloadIcon = Constants.downloadIcon;
  readonly cancelIcon = Constants.cancelIcon;
  readonly privacyPolicyRoute = APP_CONSTANTS.ROUTES.PRIVACY_POLICY;
  readonly studentOnlyAccessMessage = getStudentOnlyAccessMessage();
  readonly isStaffPortalPreview = this.authService.isStaffPortalPreviewActive();

  studentName = '';
  studentEmail = '';
  categoryOptions: { label: string; value: string }[] = [];
  selectedFile: File | null = null;
  isSubmitting = false;
  pdfViewerVisible = false;
  pdfViewerUrl = '';
  pdfViewerDocumentName = '';

  readonly form = this.fb.group({
    categoryId: [null as string | null, Validators.required],
    description: ['', [Validators.required, Validators.maxLength(2000)]]
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue !== true) {
      return;
    }
    if (changes['visible']?.previousValue !== true) {
      this.activityTracking.track(StudentActivityType.OpenComplaintModal, { pageName: 'Complaint' });
    }
    this.resetForm();
    this.loadUser();
    if (this.categoryOptions.length === 0) {
      this.loadCategories();
    }
  }

  ngOnDestroy(): void {
    this.onClosePdfViewer();
  }

  get fileIcon(): string {
    return Constants.getFileIcon(this.selectedFile?.name);
  }

  get fileSizeLabel(): string {
    return this.selectedFile ? Constants.formatBytes(this.selectedFile.size) : '';
  }

  get isDocOrDocx(): boolean {
    const name = this.selectedFile?.name?.toLowerCase() ?? '';
    return name.endsWith('.doc') || name.endsWith('.docx');
  }

  onVisibleChange(value: boolean): void {
    this.visible = value;
    this.visibleChange.emit(value);
    if (!value) {
      this.resetForm();
      this.onClosePdfViewer();
    }
  }

  onCancel(): void {
    this.onVisibleChange(false);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.selectedFile = file;
    input.value = '';
  }

  removeFile(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.selectedFile = null;
  }

  viewFile(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.selectedFile) {
      return;
    }

    const fileName = this.selectedFile.name;
    if (this.isDocOrDocx) {
      this.documentViewService.downloadBlob(this.selectedFile, fileName);
      return;
    }

    this.documentViewService.revokeUrl(this.pdfViewerUrl);
    this.pdfViewerUrl = URL.createObjectURL(this.selectedFile);
    this.pdfViewerDocumentName = fileName;
    this.pdfViewerVisible = true;
  }

  onClosePdfViewer(): void {
    this.pdfViewerVisible = false;
    this.documentViewService.revokeUrl(this.pdfViewerUrl);
    this.pdfViewerUrl = '';
    this.pdfViewerDocumentName = '';
  }

  onSubmit(): void {
    if (this.isStaffPortalPreview) {
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid || this.isSubmitting) {
      return;
    }

    const categoryId = this.form.controls.categoryId.value;
    const description = this.form.controls.description.value?.trim();
    if (!categoryId || !description) {
      return;
    }

    const formData = new FormData();
    formData.append('CategoryId', categoryId);
    formData.append('Description', description);
    if (this.selectedFile) {
      formData.append('file', this.selectedFile, this.selectedFile.name);
    }

    this.isSubmitting = true;
    this.studentService.submitComplaint(formData).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.messageService.add({
          ...APP_CONSTANTS.TOAST.SUCCESS,
          detail: 'Your complaint has been submitted.',
          life: 4000
        });
        this.activityTracking.track(StudentActivityType.SubmitComplaint, {
          pageName: 'Complaint',
          entityId: categoryId,
          metadata: { categoryId }
        });
        this.onVisibleChange(false);
      },
      error: () => {
        this.isSubmitting = false;
      }
    });
  }

  private loadUser(): void {
    const user = this.authService.getCurrentUser();
    this.studentName = user?.fullName?.trim() || '';
    this.studentEmail = user?.email?.trim() || '';
  }

  private resetForm(): void {
    this.form.reset();
    this.selectedFile = null;
    this.isSubmitting = false;
    if (this.isStaffPortalPreview) {
      this.form.disable();
    }
  }

  private loadCategories(): void {
    this.studentService.getReferenceData({ entityNames: [ReferenceDataEntity.ComplaintCategory] }).subscribe({
      next: (response) => {
        const data = response.output?.data as Record<string, unknown> | undefined;
        const items = getReferenceDataItems<ReferenceDataItem>(data, ReferenceDataEntity.ComplaintCategory);
        this.categoryOptions = items
          .slice()
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((item) => ({ label: item.entityDisplayValue, value: item.id }));
      }
    });
  }
}
